import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { TextField, Button, Stack, Typography, Grid } from "@mui/material";
import {
  postSpeaker,
  putSpeaker,
  getSpeakerById,
  uploadSpeakerImage,
} from "../../../Services/Speaker";
import { toast } from "react-toastify";

export const SpeakerDetails = () => {
  const navigate = useNavigate();
  const { speakerId } = useParams();
  const isEditing = speakerId && speakerId !== "New";

  const [name, setName] = useState("");
  const [jobRole, setJobRole] = useState("");
  const [description, setDescription] = useState("");
  const [linkedinUrl, setLinkedinUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [existingImageUrl, setExistingImageUrl] = useState<string | null>(null);

  // Preview da imagem ao selecionar
  useEffect(() => {
    if (imageFile) {
      const url = URL.createObjectURL(imageFile);
      setImagePreview(url);
      return () => URL.revokeObjectURL(url);
    } else {
      setImagePreview(null);
    }
  }, [imageFile]);

  useEffect(() => {
    if (isEditing && speakerId) {
      setLoading(true);
      getSpeakerById(speakerId).then((res: any) => {
        const data = res?.data?.data;
        if (data) {
          setName(data.name || "");
          setJobRole(data.jobRole || "");
          setDescription(data.description || "");
          setLinkedinUrl(data.linkedinUrl || "");

          // Configura a URL da imagem existente
          if (data.picture) {
            const fullImageUrl = `${process.env.REACT_APP_DIGITAL_API_URL}${data.picture}`;
            setExistingImageUrl(fullImageUrl);
          }
        }
        setLoading(false);
      });
    }
  }, [isEditing, speakerId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const payload = { name, jobRole, description, linkedinUrl };
    try {
      if (isEditing && speakerId) {
        await putSpeaker(speakerId, payload);
        if (imageFile) {
          try {
            await uploadSpeakerImage(speakerId, imageFile);
          } catch (err: any) {
            toast.error(
              "Erro ao enviar imagem do palestrante. Tente novamente."
            );
            setLoading(false);
            return;
          }
        }
        setLoading(false);
        navigate("/speaker");
      } else {
        // Primeiro cadastra o palestrante, depois faz upload da imagem se houver
        const res: any = await postSpeaker(payload);
        const newId = res?.data?.data?.id;
        if (imageFile && newId) {
          try {
            await uploadSpeakerImage(newId, imageFile);
          } catch (err: any) {
            toast.error(
              "Erro ao enviar imagem do palestrante. Tente novamente."
            );
            setLoading(false);
            return;
          }
        }
        setLoading(false);
        navigate("/speaker");
      }
    } catch (err: any) {
      toast.error("Erro ao salvar palestrante. Tente novamente.");
      setLoading(false);
    }
  };

  return (
    <Stack spacing={2} maxWidth={700} margin="auto">
      <Typography variant="h5" fontWeight={600}>
        {isEditing ? "Editar Palestrante" : "Cadastrar Palestrante"}
      </Typography>
      <form onSubmit={handleSubmit}>
        <Grid container spacing={3} alignItems="flex-start">
          {/* Linha 1: Nome | Cargo */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Nome"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Cargo"
              value={jobRole}
              onChange={(e) => setJobRole(e.target.value)}
              required
              fullWidth
            />
          </Grid>
          {/* Linha 2: LinkedIn | Upload */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="LinkedIn"
              value={linkedinUrl}
              onChange={(e) => setLinkedinUrl(e.target.value)}
              required
              fullWidth
              type="url"
            />
          </Grid>
          <Grid
            item
            xs={12}
            sm={6}
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="flex-start"
          >
            <Button
              variant="outlined"
              component="label"
              fullWidth
              style={{ height: 56 }}
            >
              {imageFile
                ? "Nova imagem selecionada"
                : existingImageUrl
                ? "Alterar imagem"
                : "Selecionar imagem de perfil"}
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={(e) => {
                  if (e.target.files && e.target.files[0]) {
                    setImageFile(e.target.files[0]);
                  }
                }}
              />
            </Button>
          </Grid>
          {/* Linha 3: Descrição | Preview */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Descrição"
              value={description}
              onChange={(e) => {
                if (e.target.value.length <= 255) {
                  setDescription(e.target.value);
                }
              }}
              required
              fullWidth
              multiline
              minRows={3}
              helperText={`${description.length}/255 caracteres`}
              inputProps={{ maxLength: 255 }}
            />
          </Grid>
          <Grid
            item
            xs={12}
            sm={6}
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="flex-start"
          >
            {(imagePreview || existingImageUrl) && (
              <img
                src={imagePreview || existingImageUrl || ""}
                alt="Preview"
                style={{
                  maxWidth: 180,
                  maxHeight: 180,
                  borderRadius: 12,
                  boxShadow: "0 2px 8px #0001",
                }}
              />
            )}
          </Grid>
          {/* Botões */}
          <Grid item xs={12}>
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button
                variant="outlined"
                onClick={() => navigate("/speaker")}
                disabled={loading}
              >
                Cancelar
              </Button>
              <Button
                variant="contained"
                color="primary"
                type="submit"
                disabled={loading}
              >
                {isEditing ? "Salvar" : "Cadastrar"}
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </form>
    </Stack>
  );
};

export default SpeakerDetails;
